/**
 * Typed client for `callout-api`.
 *
 * Auth: every request carries the first-party session JWT from callout-auth as a Bearer token.
 * The Worker verifies it against the issuer's JWKS (workers/shared/src/auth.ts) and uses `sub`
 * (the Callout `User.id`) as the User.id reference. The token comes from `./token` — the one
 * session store — and a 401 on any call marks the session dead there, so the shell can explain
 * why the coach was interrupted instead of just failing the request.
 */
import { markSessionExpired } from './token';

export const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8787').replace(
  /\/+$/,
  '',
);

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Session token from `useTokenSource().getToken()`. Omit for public endpoints. */
  token?: string | null;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    // 401 = the session we sent is no longer accepted (lapsed or revoked elsewhere). Record it
    // once, centrally: this is the single place that decides a session is dead.
    if (res.status === 401) markSessionExpired();
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText);
  }
  return payload as T;
}

export interface HealthResponse {
  ok: boolean;
  worker: string;
  version: string;
}

export function getHealth(worker = ''): Promise<HealthResponse> {
  return apiFetch<HealthResponse>(`/health${worker ? `?worker=${worker}` : ''}`);
}

export interface MeResponse {
  user: { id: string; email?: string; name?: string | null; subscription_tier?: string } | null;
  sessionId: string | null;
}

/** The signed-in coach. `user.id` is the Clerk `sub`, which is also User.id in D1. */
export function getMe(token: string | null): Promise<MeResponse> {
  return apiFetch<MeResponse>(endpoints.me(), { token });
}

/** Endpoint surface owned by callout-api (implementation lands with the pipeline cards). */
export const endpoints = {
  me: () => '/api/me',
  footage: () => '/api/footage',
  footageUpload: () => '/api/footage/upload',
  footageMultipartStart: () => '/api/footage/multipart/start',
  footageMultipartComplete: () => '/api/footage/multipart/complete',
  footageMultipartAbort: () => '/api/footage/multipart/abort',
  footageOne: (id: string) => `/api/footage/${id}`,
  footageMedia: (id: string) => `/api/footage/${id}/media`,
  sessions: () => '/api/sessions',
  sessionOne: (id: string) => `/api/sessions/${id}`,
  sessionAudio: (id: string) => `/api/sessions/${id}/audio`,
  // PIPELINE lane (card t_8f544311): the two calls that turn recorded audio into clips.
  // `/transcribe` fans out to callout-transcribe (OpenAI gpt-4o-mini-transcribe) and persists
  // the anchored transcript; `/structure` fans out to callout-structure and persists the
  // proposed clips the review deck renders.
  sessionTranscribe: (id: string) => `/api/sessions/${id}/transcribe`,
  sessionStructure: (id: string) => `/api/sessions/${id}/structure`,
  sessionReviewComplete: (id: string) => `/api/sessions/${id}/review-complete`,
  clips: (sessionId: string) => `/api/sessions/${sessionId}/clips`,
  clip: (clipId: string) => `/api/clips/${clipId}`,
  clipStatus: (clipId: string) => `/api/clips/${clipId}/status`,
  clipPreview: (clipId: string) => `/api/clips/${clipId}/preview`,
  vocabulary: (sport: string) => `/api/vocab/${sport}`,
  renderJobs: (sessionId?: string) =>
    sessionId ? `/api/render-jobs?sessionId=${encodeURIComponent(sessionId)}` : '/api/render-jobs',
  renderJob: (id: string) => `/api/render-jobs/${id}`,
  branding: (userId: string) => `/api/branding/${userId}`,
  // EXPORT lane (card t_31859fc7)
  clipShare: (clipId: string) => `/api/clips/${clipId}/share`,
  shares: () => '/api/shares',
  share: (shareLinkId: string) => `/api/shares/${shareLinkId}`,
  reels: () => '/api/reels',
  reelMedia: (reelId: string) => `/api/reels/${reelId}/media`,
  usage: () => '/api/user/usage',
  userClips: () => '/api/user/clips',
  seasons: () => '/api/seasons',
  account: () => '/api/account',
  retentionRun: () => '/api/retention/run',
} as const;

// ---------------------------------------------------------------------------
// Capture-card contracts (kept in sync with workers/api/src/index.ts)
// ---------------------------------------------------------------------------

export type FootageStatus = 'uploading' | 'transcoding' | 'ready' | 'error';
export type ConsentStatus = 'none' | 'pending' | 'granted';

export interface FootageAsset {
  id: string;
  owner_id: string;
  filename: string;
  storage_key: string;
  duration_seconds: number | null;
  resolution: string | null;
  codec: string | null;
  size_bytes: number | null;
  source: 'upload' | 'link';
  status: FootageStatus;
  consent_status: ConsentStatus;
  consent_logged_at: string | null;
  retention_date: string | null;
  created_at: string;
}

export interface SignedMedia {
  url: string;
  expiresAt: string;
  expiresInSeconds: number;
}

export type SessionStatusRow =
  | 'recording'
  | 'transcribing'
  | 'structuring'
  | 'review'
  | 'rendering'
  | 'complete'
  | 'error';

export interface TaggingSession {
  id: string;
  user_id: string;
  footage_id: string;
  vocabulary_id: string | null;
  sport: string;
  status: SessionStatusRow;
  audio_storage_key: string | null;
  transcript_raw: string | null;
  transcript_timeline: { text: string; start_s: number; end_s: number }[] | null;
  anchor_pairs: { video_time: number; wall_time: number }[] | null;
  capability_report: unknown;
  clip_count_proposed: number;
  clip_count_confirmed: number;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface AudioChunkRecord {
  storageKey: string;
  chunkIndex: number;
  bytes: number;
  chunkStartWallMs: number | null;
  durationMs: number | null;
}

export interface UploadMeta {
  filename: string;
  durationSeconds?: number | null;
  resolution?: string | null;
  codec?: string | null;
  sizeBytes?: number | null;
  containsMinors: boolean;
  consentGranted: boolean;
}

export interface UploadHandle {
  promise: Promise<{ footage: FootageAsset; media: SignedMedia }>;
  abort: () => void;
}

/**
 * Multipart upload with real progress. `fetch` cannot report upload progress, so this uses
 * XHR — the progress bar in the UI is driven by these events.
 */
export function uploadFootage(
  file: Blob,
  meta: UploadMeta,
  { token, onProgress }: { token: string | null; onProgress?: (loaded: number, total: number) => void },
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const form = new FormData();
  // Fields FIRST, file LAST: the worker streams the file part and needs the gate fields
  // (containsMinors / consentGranted) parsed before it decides whether to store anything.
  form.append('filename', meta.filename);
  if (meta.durationSeconds != null) form.append('durationSeconds', String(meta.durationSeconds));
  if (meta.resolution) form.append('resolution', meta.resolution);
  if (meta.codec) form.append('codec', meta.codec);
  if (meta.sizeBytes != null) form.append('sizeBytes', String(meta.sizeBytes));
  form.append('containsMinors', String(meta.containsMinors));
  form.append('consentGranted', String(meta.consentGranted));
  form.append('file', file, meta.filename);

  const promise = new Promise<{ footage: FootageAsset; media: SignedMedia }>((resolve, reject) => {
    xhr.open('POST', `${API_BASE}${endpoints.footageUpload()}`);
    xhr.setRequestHeader('Accept', 'application/json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    });
    xhr.addEventListener('load', () => {
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload as { footage: FootageAsset; media: SignedMedia });
        return;
      }
      if (xhr.status === 401) markSessionExpired();
      const err = (payload as ApiErrorBody | null)?.error;
      reject(new ApiError(xhr.status, err?.code ?? 'unknown', err?.message ?? xhr.statusText));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'network', 'Upload failed — check your connection')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'aborted', 'Upload cancelled')));
    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}

export interface MultipartUpload {
  footageId: string;
  uploadId: string;
  storageKey: string;
  sizeBytes: number;
  partSize: number;
  partCount: number;
  parts: { partNumber: number; url: string }[];
  expiresAt: string;
}

/** How many parts are in flight at once — enough to hide latency, few enough to stay polite. */
const MULTIPART_CONCURRENCY = 3;

/**
 * Uploads one part to its signed URL. `fetch` cannot report upload progress, so this uses XHR —
 * the same reason the single-shot path does. The URL's signature is the credential, so no
 * Authorization header is sent.
 */
function putPart(
  url: string,
  blob: Blob,
  onProgress: (sent: number) => void,
  signal: AbortSignal,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    });
    xhr.addEventListener('load', () => {
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = (payload as { part?: { etag?: string } } | null)?.part?.etag;
        if (!etag) {
          reject(new ApiError(500, 'bad_response', 'Part upload returned no etag'));
          return;
        }
        resolve(etag);
        return;
      }
      const err = (payload as ApiErrorBody | null)?.error;
      reject(new ApiError(xhr.status, err?.code ?? 'unknown', err?.message ?? xhr.statusText));
    });
    xhr.addEventListener('error', () =>
      reject(new ApiError(0, 'network', 'Part upload failed — check your connection')),
    );
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'aborted', 'Upload cancelled')));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

/**
 * R2 multipart upload for footage too large for one request body: start (server creates the R2
 * upload and hands back a signed URL per part), PUT every part, then complete (server assembles
 * the object and creates the FootageAsset row). Progress is the sum of the parts' bytes, so the
 * same progress bar drives both paths.
 */
export function uploadFootageMultipart(
  file: Blob,
  meta: UploadMeta,
  { token, onProgress }: { token: string | null; onProgress?: (loaded: number, total: number) => void },
): UploadHandle {
  const controller = new AbortController();
  const { signal } = controller;
  let uploadId: string | null = null;

  const promise = (async () => {
    const started = await apiFetch<{ upload: MultipartUpload }>(endpoints.footageMultipartStart(), {
      method: 'POST',
      token,
      signal,
      body: {
        filename: meta.filename,
        sizeBytes: file.size,
        durationSeconds: meta.durationSeconds ?? null,
        resolution: meta.resolution ?? null,
        codec: meta.codec ?? null,
        containsMinors: meta.containsMinors,
        consentGranted: meta.consentGranted,
      },
    });
    const upload = started.upload;
    uploadId = upload.uploadId;

    const sent = new Array<number>(upload.parts.length).fill(0);
    const uploaded = new Array<{ partNumber: number; etag: string } | undefined>(upload.parts.length);
    const report = () => onProgress?.(sent.reduce((a, b) => a + b, 0), file.size);
    report();

    let next = 0;
    const pump = async () => {
      for (;;) {
        const index = next;
        next += 1;
        const part = upload.parts[index];
        if (!part) return;
        const offset = index * upload.partSize;
        const blob = file.slice(offset, Math.min(offset + upload.partSize, file.size));
        const etag = await putPart(
          part.url,
          blob,
          (bytes) => {
            sent[index] = bytes;
            report();
          },
          signal,
        );
        sent[index] = blob.size;
        uploaded[index] = { partNumber: part.partNumber, etag };
        report();
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MULTIPART_CONCURRENCY, upload.parts.length) }, () => pump()),
    );

    const parts = uploaded.filter((p): p is { partNumber: number; etag: string } => p !== undefined);
    return apiFetch<{ footage: FootageAsset; media: SignedMedia }>(endpoints.footageMultipartComplete(), {
      method: 'POST',
      token,
      signal,
      body: { uploadId: upload.uploadId, parts },
    });
  })();

  return {
    promise,
    abort: () => {
      controller.abort();
      // Best-effort: release the R2 multipart upload so its parts are not left dangling.
      const id = uploadId;
      if (id) {
        void apiFetch(endpoints.footageMultipartAbort(), {
          method: 'POST',
          token,
          body: { uploadId: id },
        }).catch(() => undefined);
      }
    },
  };
}

/** Uploads one pre-processed 16 kHz mono WAV chunk (plan §1.3b / §4 limits). */
export async function uploadAudioChunk(
  sessionId: string,
  wav: Blob,
  meta: { chunkIndex: number; chunkStartWallMs: number; durationMs: number },
  token: string | null,
): Promise<AudioChunkRecord> {
  const form = new FormData();
  form.append('file', wav, `chunk-${meta.chunkIndex}.wav`);
  form.append('chunkIndex', String(meta.chunkIndex));
  form.append('chunkStartWallMs', String(meta.chunkStartWallMs));
  form.append('chunkDurationMs', String(meta.durationMs));

  const res = await fetch(`${API_BASE}${endpoints.sessionAudio(sessionId)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as { chunk?: AudioChunkRecord } & ApiErrorBody) : null;
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error?.code ?? 'unknown', payload?.error?.message ?? res.statusText);
  }
  if (!payload?.chunk) throw new ApiError(500, 'bad_response', 'Audio chunk upload returned no chunk record');
  return payload.chunk;
}

export async function getFootage(
  id: string,
  token: string | null,
): Promise<{ footage: FootageAsset; media: SignedMedia }> {
  return apiFetch(`/api/footage/${id}`, { token });
}

export async function createSession(
  footageId: string,
  token: string | null,
  extra: { sport?: string; capabilityReport?: unknown } = {},
): Promise<{ session: TaggingSession }> {
  const created = await apiFetch<{ session: TaggingSession }>(endpoints.sessions(), {
    method: 'POST',
    token,
    body: { footageId, sport: extra.sport ?? 'rugby-league' },
  });
  if (extra.capabilityReport !== undefined) {
    await updateSession(created.session.id, { capabilityReport: extra.capabilityReport }, token);
  }
  return created;
}

export interface SessionPatch {
  status?: SessionStatusRow;
  audioStorageKey?: string;
  audioChunks?: AudioChunkRecord[];
  transcriptRaw?: string;
  transcriptTimeline?: { text: string; startS: number; endS: number }[];
  anchorPairs?: { videoTime: number; wallTime: number }[];
  capabilityReport?: unknown;
  error?: string | null;
}

export async function updateSession(
  sessionId: string,
  patch: SessionPatch,
  token: string | null,
): Promise<{ session: TaggingSession }> {
  return apiFetch(endpoints.sessionOne(sessionId), { method: 'PUT', token, body: patch });
}

export async function getSession(
  sessionId: string,
  token: string | null,
): Promise<{ session: TaggingSession; footage: FootageAsset | null; media: SignedMedia | null; audioManifest: unknown }> {
  return apiFetch(endpoints.sessionOne(sessionId), { token });
}

// ---------------------------------------------------------------------------
// PIPELINE-card contracts (card t_8f544311 — the SPA must ASK for a transcript)
// ---------------------------------------------------------------------------

/** One transcript line as `POST /api/sessions/:id/transcribe` returns it (video time). */
export interface TranscriptSegmentRow {
  text: string;
  start_s: number;
  end_s: number;
}

export interface TranscribeResult {
  session: TaggingSession | null;
  transcript: {
    text: string;
    segments: TranscriptSegmentRow[];
    /** True when the segments were projected onto video time through the wall-clock anchors. */
    anchored: boolean;
    storageKey: string;
    model: string;
    upstreamCalls: number;
    sliceCount: number;
    limitReason: 'seconds' | 'bytes' | null;
    totalDurationSeconds: number;
    costUsd: number;
    errors: { chunkIndex: number; message: string }[];
  };
  message?: string;
}

/**
 * POST /api/sessions/:id/transcribe — the request the SPA never made.
 *
 * `chunks` is the exact list of audio parts the capture layer uploaded (storageKey + chunkIndex +
 * wall-clock start), so the worker reads them straight out of R2 and the transcript comes back
 * already anchored to video time. Passing them explicitly also means the call does not depend on
 * the session's R2 manifest having been written first.
 */
export function transcribeSession(
  sessionId: string,
  token: string | null,
  body: { chunks?: AudioChunkRecord[]; language?: string } = {},
): Promise<TranscribeResult> {
  return apiFetch<TranscribeResult>(endpoints.sessionTranscribe(sessionId), {
    method: 'POST',
    token,
    body,
  });
}

export interface StructureResult {
  session: TaggingSession | null;
  clips: StructuredClipRow[];
  structuring: {
    model: string;
    accepted: number;
    dropped: number;
    clipLimit: number;
    latencyMs: number;
    costUsd: number | null;
  };
  message?: string;
}

/**
 * POST /api/sessions/:id/structure — the second half of the pipeline: the transcript becomes
 * clip proposals. Called after `transcribeSession` so the worker reads the transcript the
 * transcribe call just persisted.
 */
export function structureSession(
  sessionId: string,
  token: string | null,
  body: { sport?: string; transcript?: TranscriptSegmentRow[]; keepExisting?: boolean } = {},
): Promise<StructureResult> {
  return apiFetch<StructureResult>(endpoints.sessionStructure(sessionId), {
    method: 'POST',
    token,
    body,
  });
}

// ---------------------------------------------------------------------------
// REVIEW-card contracts (kept in sync with workers/api/src/index.ts)
// ---------------------------------------------------------------------------

export type ClipStatus = 'proposed' | 'confirmed' | 'edited' | 'rejected' | 'rendered';

/** One AI-proposed clip exactly as D1 stores it (edited_* = the coach's override, if any). */
export interface StructuredClipRow {
  id: string;
  session_id: string;
  t_in: number;
  t_out: number;
  label: string;
  vocab_ref: string | null;
  notes: string;
  confidence: number;
  flags: string[];
  status: ClipStatus;
  edited_label: string | null;
  edited_notes: string | null;
  edited_t_in: number | null;
  edited_t_out: number | null;
  edited_vocab_ref: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface VocabularyTerm {
  key: string;
  label: string;
  category?: string;
  aliases: string[];
}

export interface SessionReviewPayload {
  session: TaggingSession;
  footage: FootageAsset | null;
  media: SignedMedia | null;
  clips: StructuredClipRow[];
  clipCountProposed: number;
  clipCountConfirmed: number;
}

/** Loads everything the review deck needs to restore a session: session row, source media, clips. */
export async function getSessionForReview(
  sessionId: string,
  token: string | null,
): Promise<SessionReviewPayload> {
  return apiFetch(endpoints.sessionOne(sessionId), { token });
}

export interface ClipPatch {
  status?: ClipStatus;
  label?: string;
  notes?: string;
  tIn?: number;
  tOut?: number;
  vocabRef?: string | null;
}

export interface ClipPatchResult {
  clip: StructuredClipRow;
  clipCountProposed: number;
  clipCountConfirmed: number;
}

export function updateClip(
  clipId: string,
  patch: ClipPatch,
  token: string | null,
): Promise<ClipPatchResult> {
  return apiFetch(endpoints.clipStatus(clipId), { method: 'PUT', token, body: patch });
}

export interface ClipPreview {
  clipId: string;
  tIn: number;
  tOut: number;
  /** v1 grabs the frame in the browser (canvas.drawImage); there is no server-side ffmpeg yet. */
  mode: 'client-canvas';
  mediaUrl: string;
  mediaExpiresAt: string;
  codec: string | null;
  width: number | null;
  height: number | null;
}

export async function getClipPreview(
  clipId: string,
  token: string | null,
): Promise<ClipPreview> {
  const body = await apiFetch<{ preview: ClipPreview }>(endpoints.clipPreview(clipId), { token });
  return body.preview;
}

export interface ReviewCompleteResult {
  session: TaggingSession;
  review: {
    completedAt: string;
    clipCountProposed: number;
    clipCountConfirmed: number;
    clipCountEdited: number;
    clipCountRejected: number;
  };
}

/** review → rendering. The worker refuses when nothing is confirmed (the review-first contract). */
export function completeReview(
  sessionId: string,
  token: string | null,
): Promise<ReviewCompleteResult> {
  return apiFetch(endpoints.sessionReviewComplete(sessionId), { method: 'POST', token, body: {} });
}

export async function getVocabulary(
  sport: string,
  token: string | null,
): Promise<{ id: string; sport: string; version: number; terms: VocabularyTerm[]; termCount: number; categories: string[] }> {
  const body = await apiFetch<{
    vocabulary: { id: string; sport: string; version: number; terms: VocabularyTerm[]; termCount: number; categories: string[] };
  }>(endpoints.vocabulary(sport), { token });
  return body.vocabulary;
}

// ---------------------------------------------------------------------------
// RENDER-card contracts (kept in sync with workers/api/src/index.ts)
// ---------------------------------------------------------------------------

export type RenderJobStatus = 'queued' | 'running' | 'complete' | 'failed';
export type RenderJobRenderer = 'webcodecs' | 'server';

/** One render attempt, as D1 stores it — the timing/size metrics the card asks to track. */
export interface RenderJobRow {
  id: string;
  clip_id: string;
  status: RenderJobStatus;
  renderer: RenderJobRenderer;
  quality: 'preview' | 'export';
  result_storage_key: string | null;
  duration_seconds: number | null;
  size_bytes: number | null;
  render_time_ms: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface RenderJobCreate {
  clipId: string;
  renderer: RenderJobRenderer;
  quality: 'preview' | 'export';
  /** Defaults to `running` — the client creates the row as it starts the encode. */
  status?: RenderJobStatus;
}

export interface RenderJobPatch {
  status?: RenderJobStatus;
  durationSeconds?: number | null;
  sizeBytes?: number | null;
  renderTimeMs?: number | null;
  resultStorageKey?: string | null;
  error?: string | null;
}

export function createRenderJob(
  body: RenderJobCreate,
  token: string | null,
): Promise<{ renderJob: RenderJobRow }> {
  return apiFetch(endpoints.renderJobs(), { method: 'POST', token, body });
}

export function updateRenderJob(
  id: string,
  patch: RenderJobPatch,
  token: string | null,
): Promise<{ renderJob: RenderJobRow }> {
  return apiFetch(endpoints.renderJob(id), { method: 'PUT', token, body: patch });
}

export function listRenderJobs(
  sessionId: string,
  token: string | null,
): Promise<{ renderJobs: RenderJobRow[] }> {
  return apiFetch(endpoints.renderJobs(sessionId), { token });
}

/** The coach's club branding; `brandingKit` is null when they have not set one up (defaults). */
export interface BrandingKitRow {
  id: string;
  owner_type: 'user' | 'club';
  owner_id: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  club_name: string | null;
}

export function getBranding(
  userId: string,
  token: string | null,
): Promise<{ brandingKit: BrandingKitRow | null }> {
  return apiFetch(endpoints.branding(userId), { token });
}

// ---------------------------------------------------------------------------
// EXPORT lane — share links, highlight reels, usage, seasons, account deletion
// (card t_31859fc7: share links, watermark, reels, free-tier limits, storage)
// ---------------------------------------------------------------------------

export type ShareKind = 'clip' | 'reel';

export interface ShareUrls {
  streamUrl: string;
  infoUrl: string;
  pageUrl: string;
  appUrl?: string;
}

export interface ShareLink {
  id: string;
  token: string;
  kind: ShareKind;
  clipId: string | null;
  clipIds: string[];
  title: string | null;
  createdAt: string;
  expiresAt: string | null;
  accessCount: number;
  lastAccessAt: string | null;
  revokedAt: string | null;
  media: { storageKey: string | null; sizeBytes: number | null; durationSeconds: number | null };
}

/** A share link plus the URLs that reach it. `pageUrl` is the self-contained share page. */
export interface ShareLinkWithUrls extends ShareLink {
  urls: ShareUrls;
}

export interface CreateShareResult {
  shareLink: ShareLink;
  urls: ShareUrls;
  share: {
    title: string;
    kind: ShareKind;
    clipId: string;
    sessionId: string;
    tIn: number;
    tOut: number;
    durationSeconds: number | null;
    sizeBytes: number | null;
    teamName: string;
  };
}

export interface CreateShareInput {
  title?: string;
  /** 0 / undefined = never expires (the card's "expires in 7 days" or "never"). */
  expiresInDays?: number;
  /**
   * The rendered MP4. The render happens on the coach's device, so this is what puts the bytes
   * in R2 for a recipient to stream; omit it only when the clip was already shared/uploaded.
   */
  media?: Blob;
  durationSeconds?: number;
}

/** POST /api/clips/:id/share — mint (and upload the media for) a clip's share link. */
export async function createClipShare(
  clipId: string,
  input: CreateShareInput,
  token: string | null,
): Promise<CreateShareResult> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit;
  if (input.media) {
    const form = new FormData();
    if (input.title) form.append('title', input.title);
    if (input.expiresInDays) form.append('expiresInDays', String(input.expiresInDays));
    if (input.durationSeconds !== undefined) {
      form.append('durationSeconds', String(input.durationSeconds));
    }
    form.append('file', input.media, `${clipId}.mp4`);
    body = form;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({
      title: input.title,
      expiresInDays: input.expiresInDays,
    });
  }

  const res = await fetch(`${API_BASE}${endpoints.clipShare(clipId)}`, {
    method: 'POST',
    headers,
    body,
  });
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText);
  }
  return payload as CreateShareResult;
}

export function listShares(
  token: string | null,
): Promise<{ shares: ShareLinkWithUrls[]; tier: string; activeShareLinks: number; activeShareLinkLimit: number }> {
  return apiFetch(endpoints.shares(), { token });
}

export function revokeShare(
  shareLinkId: string,
  token: string | null,
): Promise<{ shareLink: ShareLink | null }> {
  return apiFetch(endpoints.share(shareLinkId), { method: 'DELETE', token });
}

export interface Reel {
  id: string;
  token: string;
  kind: ShareKind;
  clipId: string | null;
  clipIds: string[];
  title: string | null;
  createdAt: string;
  expiresAt: string | null;
  accessCount: number;
  lastAccessAt: string | null;
  revokedAt: string | null;
  media: { storageKey: string | null; sizeBytes: number | null; durationSeconds: number | null };
  clipCount?: number;
  urls?: ShareUrls;
}

export interface CreateReelInput {
  title: string;
  /** The confirmed clips, in the order they should play. */
  clipIds: string[];
  /** The stitched MP4, when the client has already rendered it. */
  media?: Blob;
  durationSeconds?: number;
  expiresInDays?: number;
}

/**
 * POST /api/reels — register the reel and mint its share link.
 *
 * Two shapes, like createClipShare: multipart when the stitched MP4 is ready (one request, one
 * link), or JSON to reserve the reel and follow with PUT /api/reels/:id/media.
 */
export async function createReel(
  input: CreateReelInput,
  token: string | null,
): Promise<{ reel: Reel; urls: ShareUrls; tier: string }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit;
  if (input.media) {
    const form = new FormData();
    form.append('title', input.title);
    form.append('clipIds', JSON.stringify(input.clipIds));
    if (input.durationSeconds !== undefined) {
      form.append('durationSeconds', String(input.durationSeconds));
    }
    if (input.expiresInDays) form.append('expiresInDays', String(input.expiresInDays));
    form.append('file', input.media, 'reel.mp4');
    body = form;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({
      title: input.title,
      clipIds: input.clipIds,
      expiresInDays: input.expiresInDays,
      durationSeconds: input.durationSeconds,
    });
  }

  const res = await fetch(`${API_BASE}${endpoints.reels()}`, { method: 'POST', headers, body });
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText);
  }
  return payload as { reel: Reel; urls: ShareUrls; tier: string };
}

export function listReels(
  token: string | null,
): Promise<{ reels: Reel[]; tier: string; reelsThisWeek: number; reelsPerWeekLimit: number }> {
  return apiFetch(endpoints.reels(), { token });
}

/** PUT /api/reels/:id/media — attach the stitched MP4 to an already-registered reel. */
export async function uploadReelMedia(
  reelId: string,
  blob: Blob,
  token: string | null,
  durationSeconds?: number,
): Promise<{ reel: Reel; urls: ShareUrls }> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'video/mp4',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const query = durationSeconds !== undefined ? `?durationSeconds=${durationSeconds}` : '';
  const res = await fetch(`${API_BASE}${endpoints.reelMedia(reelId)}${query}`, {
    method: 'PUT',
    headers,
    body: blob,
  });
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText);
  }
  return payload as { reel: Reel; urls: ShareUrls };
}

export interface TierLimits {
  footageMinutesPerSession: number;
  sessionsPerWeek: number;
  clipsPerSession: number;
  clipMinSeconds: number;
  clipMaxSeconds: number;
  retentionMonths: number;
  reelsPerWeek: number;
  reelMaxClips: number;
  reelMinClips: number;
  activeShareLinks: number;
}

export interface UsageResponse {
  tier: string;
  limits: TierLimits;
  retention: { months: number; label: string; warning: string; expiringNow: number };
  usage: {
    sessionsThisWeek: number;
    sessionsThisWeekLimit: number;
    weekStart: string;
    sessionsTotal: number;
    clipsTotal: number;
    clipsConfirmed: number;
    clipsPerSessionLimit: number;
    footageCount: number;
    footageSeconds: number;
    footageMinutesPerSessionLimit: number;
    storageBytes: number;
    footageBytes: number;
    clipBytes: number;
    reelBytes: number;
    reelsThisWeek: number;
    reelsPerWeekLimit: number;
    activeShareLinks: number;
    activeShareLinkLimit: number;
  };
  subscription: { tier: string; proAvailable: boolean; upgradeMessage: string; watermark: boolean };
}

export function getUserUsage(token: string | null): Promise<UsageResponse> {
  return apiFetch(endpoints.usage(), { token });
}

export interface BrowsedClip {
  id: string;
  session_id: string;
  t_in: number;
  t_out: number;
  label: string;
  edited_label: string | null;
  edited_t_in: number | null;
  edited_t_out: number | null;
  status: ClipStatus;
  vocab_ref: string | null;
  edited_vocab_ref: string | null;
  confidence: number;
  flags: string[];
  rendered_storage_key: string | null;
  created_at: string;
  season: string;
  session_status: string;
  footage_filename: string | null;
  shared: boolean;
}

export interface BrowseClipsResult {
  clips: BrowsedClip[];
  pagination: { limit: number; offset: number; total: number; nextOffset: number | null };
  filters: { season: string | null; sessionId: string | null; status: string | null };
}

export function browseClips(
  query: { season?: string; sessionId?: string; status?: string; limit?: number; offset?: number },
  token: string | null,
): Promise<BrowseClipsResult> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return apiFetch(`${endpoints.userClips()}${qs ? `?${qs}` : ''}`, { token });
}

export interface SeasonSummary {
  season: string;
  sessions: number;
  clips: number;
  confirmedClips: number;
}

export function getSeasons(token: string | null): Promise<{ seasons: SeasonSummary[] }> {
  return apiFetch(endpoints.seasons(), { token });
}

export interface AccountDeletionResult {
  deleted: Record<string, number>;
  mediaObjects: number;
  mediaKeys: string[];
  prefixesPurged: string[];
  deletedAt: string;
  cascadeComplete: boolean;
  confirmationEmail: { to: string; subject: string; body: string; stub: boolean };
}

/** DELETE /api/account?confirm=delete — the cascade purge. `confirm` is required by the worker. */
export function deleteAccount(
  confirm: boolean,
  token: string | null,
): Promise<AccountDeletionResult> {
  return apiFetch(`${endpoints.account()}${confirm ? '?confirm=delete' : ''}`, {
    method: 'DELETE',
    token,
  });
}

export function runRetention(
  token: string | null,
): Promise<{ retention: Record<string, unknown>; remainingMediaObjects: number }> {
  return apiFetch(endpoints.retentionRun(), { method: 'POST', token });
}

/** Fetches a share's public metadata without a token — what a recipient's page shows. */
export async function getShareInfo(token: string): Promise<{
  share: {
    token: string;
    kind: ShareKind;
    title: string;
    clipCount: number;
    durationSeconds: number | null;
    sizeBytes: number | null;
    teamName: string;
    season: string | null;
    createdAt: string;
    expiresAt: string | null;
    accessCount: number;
    lastAccessAt: string | null;
    revoked: boolean;
  };
  urls: ShareUrls;
}> {
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(token)}/info`);
  if (!res.ok) throw new ApiError(res.status, 'not_found', 'This share link is not available');
  return (await res.json()) as Awaited<ReturnType<typeof getShareInfo>>;
}
